// packages/core/src/learning/school-modules/career-capital.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Career capital". surface: 'school',
// homeSchool: 'career-capital'. module id 'school-career-capital' matches the
// exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #d8612b) — flag for the presentation-tuning pass.
// tier/pillar: income-domain lens (SchoolMeta bucket 'income-life-domains') →
// tier 'growth', pillar 'income-career-lever' (core module 08, Income Is a
// Lever).
//
// SOURCE-REPAIR NOTE: lesson 05's audioScript contained a garbled, duplicated
// run in the source ("...stay in a system that keeps taking without you
// responsibility without authority. Call you essential but deny growth. Use
// your ambition against returning real opportunity."). Repaired to clean prose
// preserving intended meaning. Flag for editorial review.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_CAREER_CAPITAL_MODULE: LearningModule = {
	id: 'school-career-capital',
	title: 'Career capital',
	description:
		'The lens that treats skill, proof, trust, and reputation as assets that travel with you — building future earning power and options instead of only surviving the next pay period.',
	emoji: '🛠️', // PLACEHOLDER — tune in presentation pass
	accentColor: '#d8612b', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'income-career-lever',
	order: 0,
	surface: 'school',
	homeSchool: 'career-capital',
	lessons: [
		{
			id: 'school-career-capital-01',
			title: 'Skills become future options',
			description:
				'This lesson teaches the central career capital lens: skill is not only job performance, it is future bargaining power.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['career-capital'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['skill-asset', 'future-options', 'bargaining-power'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['skill asset', 'future options', 'bargaining power'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Career capital starts with a simple idea: some skills travel with you. A paycheck pays you now, but skill can keep paying later through better options.',
				},
				{
					type: 'paragraph',
					text: 'This lens is paying attention to what you are becoming while you work. A job is not only hours traded for money. It can also be a training ground for judgment, proof, confidence, relationships, and future leverage.',
				},
				{
					type: 'bullet_list',
					items: [
						'Build rare skills',
						'Solve harder problems',
						'Turn experience into proof',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating work as only survival while ignoring what it could build for your future self.',
				},
				{
					type: 'example',
					text: 'Two people may have the same title today. One repeats the same tasks for years. The other learns harder systems, documents results, earns trust, and becomes easier to recommend. Their future options may look very different.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that skill building can be slow. It may require boredom, feedback, failure, difficult projects, and seasons where progress is not immediately paid.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to income and runway. You are asking whether your current work is only funding life today or also building options for tomorrow.',
				},
				{
					type: 'key_takeaway',
					text: 'Career capital is skill that can become future choice.',
				},
			],
			audioScript:
				'Career capital starts with a different way to look at work. A job is not only a paycheck. A job can also be a place where you build assets that travel with you.\n\nThose assets are not always visible on a bank statement. Skill. Judgment. Reputation. Proof. Confidence. Relationships. The ability to solve harder problems. The ability to explain your work clearly. The ability to be trusted with more responsibility. These things can become future options.\n\nThat is the core lens. Career capital believes that what you are becoming while you work matters. You may be getting paid for the task in front of you, but you may also be building a stronger version of yourself for the next role, the next negotiation, the next business idea, or the next move.\n\nThink about two people with the same job title. One person does only what is assigned, avoids learning anything difficult, and hopes time alone creates progress. The other person takes on harder problems, asks better questions, improves a messy process, learns the system, documents results, and becomes the person others trust when things get complicated. They may look similar on paper for a while. Over time, their options can separate.\n\nThat is why career capital matters. It is the slow build before the visible jump.\n\nThis does not mean worshiping work. It does not mean giving everything to a company. It does not mean accepting burnout because someday it might pay off. That is where the lens can get abused. Career capital should build your options, not make you a permanent servant to someone else’s goals.\n\nThe useful version asks: what skill am I building that belongs to me? What proof can I point to? What would make me more valuable in the market, not just busier in my current job? What hard thing could I learn that would open future doors?\n\nThe hard part is that career capital often requires patience. You may not get rewarded immediately. You may have to take feedback. You may have to do difficult work before it looks impressive. You may have to build quietly for months before the result turns into income. That can be frustrating, especially when bills are real and you need money now.\n\nInside Nisk, this lens connects to income and runway because career options affect your money life. Higher earning power can change how fast you pay down debt, build savings, invest, move, or create breathing room. Runway also matters because it gives you more freedom to choose better work instead of clinging to any paycheck.\n\nCareer capital is not about status. It is about becoming harder to replace and easier to recommend.',
			flashcards: [
				{
					id: 'school-career-capital-01-fc-1',
					role: 'main_idea',
					front: 'What is career capital?',
					back: 'Skills, proof, trust, and reputation that create future options.',
				},
				{
					id: 'school-career-capital-01-fc-2',
					role: 'watch_out',
					front: 'What is the job trap?',
					back: 'Treating work only as survival while building no future leverage.',
				},
				{
					id: 'school-career-capital-01-fc-3',
					role: 'use_it',
					front: 'How do you build it?',
					back: 'Learn harder skills and turn work into visible proof.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name your skill asset',
				prompt: 'Write one skill you are building that could travel with you into a better opportunity.',
			},
			revision: 1,
		},
		{
			id: 'school-career-capital-02',
			title: 'Reputation compounds quietly',
			description:
				'This lesson teaches how trust, reliability, judgment, and visibility become career assets over time.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['career-capital'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['reputation-capital', 'trust-signal', 'visibility-loop'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['reputation capital', 'trust signal', 'visibility loop'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Career capital is not only technical skill. People also remember whether they can trust you.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes reputation compounds quietly. When people see you deliver, learn, communicate, and handle pressure, your name starts carrying weight before you enter the room.',
				},
				{
					type: 'bullet_list',
					items: [
						'Deliver what you promise',
						'Communicate before things break',
						'Make good work easy to notice',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is doing solid work in silence and assuming the right people will automatically notice.',
				},
				{
					type: 'example',
					text: 'Someone who fixes a difficult issue and explains the result clearly becomes easier to trust. Someone who does the work but leaves everyone confused may miss the reputation value.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that visibility can become performance. The goal is not to brag constantly. The goal is to make real value legible.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a reflection on earning power. You are asking whether your work is building trust that could support future income, freedom, and choices.',
				},
				{
					type: 'key_takeaway',
					text: 'Reputation is career capital when real value becomes easy for others to trust.',
				},
			],
			audioScript:
				'Career capital is not only about being good at the work. It is also about being trusted.\n\nThat can be uncomfortable to admit because people like to believe skill alone wins. Skill matters. But in most careers, people do not only ask, can this person do the task? They ask, can I trust this person with the task? Will they communicate? Will they follow through? Will they tell me early if something is blocked? Will they handle feedback? Will they make the team stronger or harder to work with?\n\nThat is reputation capital.\n\nReputation compounds quietly because every project leaves a signal. You either become someone people trust more, trust less, or barely remember. Over time, those signals stack. A manager remembers who stayed calm during a hard week. A coworker remembers who helped without making it weird. A client remembers who explained the issue clearly. A team remembers who did not hide problems until the last second.\n\nThis matters because opportunities often move through trust before they move through formal systems. A better project. A referral. A promotion conversation. A chance to lead. A recommendation. A new role. A side opportunity. Reputation can open doors that a resume alone cannot.\n\nBut there is a common mistake: doing good work in silence and assuming the right people will notice. Sometimes they do. Often they do not. Workplaces are busy. Managers are distracted. Teams are messy. If your value is invisible, it may not turn into career capital.\n\nThat does not mean bragging. It means making value legible. Clear updates. Before and after notes. Results stated plainly. Problems explained without drama. Wins connected to business value. Lessons shared. Credit given fairly. This is not fake performance. It is communication.\n\nThere is a blind spot. Visibility can become theater. Some people get good at looking valuable without being valuable. Some workplaces reward noise more than substance. That is real. The career capital lens should not pretend every system is fair. But it still says that hiding your real value is rarely the best answer.\n\nInside Nisk, this matters because reputation can affect income and options. More trust can lead to better roles, better pay, more flexibility, and more choice. Not guaranteed. Not automatic. But worth building.\n\nA useful reflection is simple: if someone described your work when you were not in the room, what would they say? Reliable? Skilled? Clear? Hard to work with? Invisible? Helpful? Replaceable? Trusted?\n\nReputation is not about being liked by everyone. It is about becoming someone whose real value is easy to trust.',
			flashcards: [
				{
					id: 'school-career-capital-02-fc-1',
					role: 'main_idea',
					front: 'What is reputation capital?',
					back: 'Trust and credibility that make your value easier to believe.',
				},
				{
					id: 'school-career-capital-02-fc-2',
					role: 'watch_out',
					front: 'What is invisible value?',
					back: 'Doing useful work that nobody understands, sees, or can repeat.',
				},
				{
					id: 'school-career-capital-02-fc-3',
					role: 'use_it',
					front: 'How do you build reputation?',
					back: 'Deliver clearly, communicate early, and make results legible.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Audit your trust signal',
				prompt: 'Write what you want trusted people to say about your work when you are not in the room.',
			},
			revision: 1,
		},
		{
			id: 'school-career-capital-03',
			title: 'Proof beats potential',
			description:
				'This lesson teaches why career capital values visible results, projects, and evidence over vague ambition.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['career-capital'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['proof-stack', 'visible-results', 'project-evidence'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['proof stack', 'visible results', 'project evidence'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Potential feels good, but proof moves faster. Career capital cares about what you can show, not only what you say you could become.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes proof turns skill into a market signal. A project, result, portfolio, case study, shipped feature, improved process, or clear metric can make your value easier to understand.',
				},
				{
					type: 'bullet_list',
					items: [
						'Show the result',
						'Name the problem',
						'Explain the impact',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is waiting to feel ready before creating evidence.',
				},
				{
					type: 'example',
					text: 'A software engineer who says they are good at backend work has a claim. A software engineer who can show cleaner endpoints, faster reporting, fewer bugs, and documented impact has proof.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that proof can be uneven. Some roles make results easier to measure than others, and some people work in systems where credit is not fairly assigned.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to future earning power. You are asking what evidence could support a better role, stronger negotiation, or more flexible path.',
				},
				{
					type: 'key_takeaway',
					text: 'Career capital grows faster when your value becomes visible proof.',
				},
			],
			audioScript:
				'Potential feels good. It tells a nice story. I could do that. I am capable. I am smart. I would be great if given the chance. There may be truth in that. But career capital is blunt about this: proof beats potential.\n\nProof is what makes your value easier for other people to believe. It is the shipped project. The improved process. The cleaner system. The client saved. The bug pattern reduced. The dashboard made faster. The team made less confused. The negotiation won. The portfolio built. The writing published. The people helped. The result made visible.\n\nThis matters because the market is noisy. Managers, clients, recruiters, coworkers, and partners cannot see your inner potential. They see signals. Some signals are shallow, like titles and brands. Some signals are stronger, like clear evidence of valuable work. Career capital pushes you toward stronger signals.\n\nA software engineer can say, I am good at backend development. That may be true. But the proof is stronger if they can show they improved API performance, reduced bugs, cleaned up a messy data flow, documented a system, shipped a feature, or helped the team move faster. A designer can say they understand users. Proof is a before and after. A salesperson can say they are persuasive. Proof is a pipeline, conversion improvement, or client story. A manager can say they lead well. Proof is retention, clarity, delivery, and people growing under them.\n\nThe trap is waiting until you feel ready before you create evidence. Many people keep learning quietly, planning quietly, and hoping quietly. Learning matters, but proof usually requires making something visible. You do not need perfect proof. You need honest proof. A small project, a clear writeup, a measurable improvement, a public portfolio, a better resume bullet, a case study, a documented win.\n\nThere are limits. Not every job makes proof easy. Some work is confidential. Some results are shared by teams. Some workplaces steal credit or reward politics. Some valuable work is hard to measure. The career capital lens should not pretend proof is always clean. But even then, you can often describe the problem, your role, the action, and the result without exaggerating.\n\nInside Nisk, this connects to income because proof can support opportunity. A better role, stronger negotiation, more flexibility, a side project, or a career pivot often needs evidence. Not guaranteed. But evidence gives you more to stand on.\n\nAsk yourself: what proof am I building this quarter? Not just what am I doing. What evidence will exist when the work is done?\n\nPotential is a promise. Proof is a signal. Career capital grows when the signal gets stronger.',
			flashcards: [
				{
					id: 'school-career-capital-03-fc-1',
					role: 'main_idea',
					front: 'Why does proof matter?',
					back: 'It turns skill into evidence other people can understand.',
				},
				{
					id: 'school-career-capital-03-fc-2',
					role: 'watch_out',
					front: 'What is potential without proof?',
					back: 'A claim that may be true but is hard to trust.',
				},
				{
					id: 'school-career-capital-03-fc-3',
					role: 'use_it',
					front: 'What proof should you build?',
					back: 'Create visible evidence of problems solved and impact created.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Build your proof stack',
				prompt: 'Write one project, result, or metric that could prove your value more clearly.',
			},
			revision: 1,
		},
		{
			id: 'school-career-capital-04',
			title: 'Career capital buys choices',
			description:
				'This lesson teaches how strong career capital can create income growth, mobility, negotiation power, and less dependence on one employer.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['career-capital'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'career-optionality',
				'income-mobility',
				'employer-dependence',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'career optionality',
				'income mobility',
				'employer dependence',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Career capital matters because it can change your choices. More skill, proof, and reputation can give you more ways to earn, move, negotiate, and recover.',
				},
				{
					type: 'paragraph',
					text: 'This lens sees career growth as a freedom lever. When your value is stronger in the market, one employer has less control over your whole life.',
				},
				{
					type: 'bullet_list',
					items: [
						'Better roles can become reachable',
						'Negotiation can have more weight',
						'Runway can make moves less desperate',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is waiting until you hate your job before building options.',
				},
				{
					type: 'example',
					text: 'Someone with strong proof, current skills, trusted references, and savings runway can approach a job change differently than someone with no proof and no cushion.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that career capital is not always rewarded fairly. Bias, bad management, weak labor markets, location, health, and family constraints can affect outcomes.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to Simulate and runway. You can test how income growth or breathing room changes your financial path without treating any outcome as guaranteed.',
				},
				{
					type: 'key_takeaway',
					text: 'Career capital is powerful because it can reduce dependence on one paycheck source.',
				},
			],
			audioScript:
				'Career capital matters because it can buy choices.\n\nNot instantly. Not perfectly. Not in every workplace. But over time, skill, proof, reputation, and judgment can change the way you move through your career. You may have more options for roles, more confidence in negotiation, more ways to earn, more ability to leave a bad environment, and more resilience if one job disappears.\n\nThat is why this lens sees career growth as a money lever. A higher income can change your savings rate, debt progress, runway, investing ability, housing choices, family support, and freedom timeline. A more flexible role can change your health, location, schedule, and quality of life. A stronger reputation can make opportunities come from people who already trust you.\n\nThe important part is dependence. If one employer is your only option, that employer has more power over your life. If your skills are stale, your proof is weak, your network is thin, and your runway is short, leaving becomes scarier. You may tolerate bad work longer because the alternative feels dangerous.\n\nCareer capital is one way to lower that dependence. You build skills that are useful beyond your current seat. You build proof that shows value. You build relationships that make you easier to recommend. You keep learning enough that your market does not pass you by. You build runway so you are not forced to grab the first option in a panic.\n\nThis does not mean chasing work at all costs. The point is not to become a productivity machine. The point is to create more room to choose. Maybe career capital helps you earn more. Maybe it helps you go remote. Maybe it helps you shift teams. Maybe it helps you freelance, consult, teach, lead, build, or move. Maybe it simply helps you say no when a workplace starts costing too much of your life.\n\nBut this lens has to stay honest. Career capital is not always rewarded fairly. Some managers are bad. Some companies underpay. Bias exists. Labor markets change. Caregiving limits availability. Health changes energy. Location matters. The strongest skill does not guarantee a clean outcome. That is why the lens should never promise that effort always wins.\n\nInside Nisk, this is where you test scenarios. What happens if income rises by $300 a month? What happens if a side skill creates extra income? What happens if runway grows enough to make a career move less desperate? The point is not to predict your future. It is to understand how career choices and financial options interact.\n\nCareer capital buys choices because it makes you less dependent on one person, one company, one title, or one paycheck.',
			flashcards: [
				{
					id: 'school-career-capital-04-fc-1',
					role: 'main_idea',
					front: 'What can career capital buy?',
					back: 'More income options, mobility, negotiation power, and less dependence.',
				},
				{
					id: 'school-career-capital-04-fc-2',
					role: 'watch_out',
					front: 'What is employer dependence?',
					back: 'Having too few options outside one job or company.',
				},
				{
					id: 'school-career-capital-04-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how income growth or runway changes your choices.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
			revision: 1,
		},
		{
			id: 'school-career-capital-05',
			title: 'Where career capital breaks down',
			description:
				'This lesson teaches the limits of career capital and why skill building should not become status chasing, burnout, or blind loyalty to work.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['career-capital'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['status-trap', 'burnout-cost', 'career-humility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['status trap', 'burnout cost', 'career humility'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Career capital is useful, but it can become toxic if every part of your life turns into a career asset.',
				},
				{
					type: 'paragraph',
					text: 'This lens breaks down when it treats status, productivity, and professional identity as the whole person. You are more than your title, output, income, and reputation.',
				},
				{
					type: 'bullet_list',
					items: [
						'Skill is not self worth',
						'Status is not freedom',
						'Burnout destroys options too',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is building a stronger resume while building a weaker life.',
				},
				{
					type: 'example',
					text: 'Someone may take every hard project, say yes to every demand, and grow fast for a while. If health, sleep, relationships, and judgment collapse, the career capital is costing too much.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is loyalty to systems that do not deserve it. Some workplaces take extra effort without giving fair pay, respect, growth, or safety back.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, the mature move is to connect career growth to the life it is meant to support. Build skill, but keep asking what options it is creating and what it is costing.',
				},
				{
					type: 'key_takeaway',
					text: 'Career capital should expand your life, not consume it.',
				},
			],
			audioScript:
				'Career capital is a strong lens, but it can become toxic if you let it take over your whole identity.\n\nThe useful version says skills, proof, reputation, and judgment can create options. The unhealthy version says every hour must become a career asset. Every relationship becomes networking. Every project becomes status. Every choice becomes resume fuel. Rest becomes guilt. Hobbies become side businesses. Your job title becomes your self worth.\n\nThat is not freedom. That is a different kind of trap.\n\nCareer capital breaks down when it forgets that you are a full person. You have health, relationships, family, culture, energy, faith, creativity, rest, and private life. Not everything needs to be optimized for career growth. A life that only looks impressive on LinkedIn can still feel empty, lonely, or exhausted in real life.\n\nThere is also the burnout problem. Some people build career capital by saying yes to everything. Hard projects, extra hours, urgent fixes, emotional labor, constant availability, weekend work, messy rescue missions. For a season, that may create skill and trust. But if the pattern never ends, the cost can become too high. Poor sleep, resentment, health problems, weaker relationships, worse judgment, and eventually less ability to perform at all.\n\nBurnout destroys options too.\n\nThis lens also has to be careful with loyalty. Some workplaces will take everything you give and still not reward you fairly. They may praise your effort but not pay you. Give you responsibility without authority. Call you essential but deny growth. Use your ambition against you. Career capital should not become an excuse to stay in a system that keeps taking without ever returning real opportunity.\n\nThe mature version asks better questions. Is this hard project building a skill I actually want? Is this role creating proof I can use later? Is this reputation worth the cost? Am I growing, or just being used? Is my income improving with my value? Is this path giving me more choices, or only more work?\n\nInside Nisk, career capital should connect back to your life. More earning power is useful because it can support freedom, runway, family, health, and choices. It is not useful if it only buys a more impressive cage.\n\nSo build skill. Build proof. Build trust. Take your growth seriously. But do not confuse your career with your whole life. Career capital is supposed to expand your options, not consume the person those options were meant to serve.',
			flashcards: [
				{
					id: 'school-career-capital-05-fc-1',
					role: 'main_idea',
					front: 'Where can career capital fail?',
					back: 'When status, productivity, or work identity consumes the whole person.',
				},
				{
					id: 'school-career-capital-05-fc-2',
					role: 'watch_out',
					front: 'What is the status trap?',
					back: 'Building a stronger resume while building a weaker life.',
				},
				{
					id: 'school-career-capital-05-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask what your career growth creates and what it costs.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check the cost',
				prompt: 'Write what your current career growth is giving you and what it is costing your health, time, and relationships.',
			},
			revision: 1,
		},
	],
};
